import React from 'react';
import { Box, MenuItem, Pagination, Select, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

export interface PaginationBarProps {
  limit: number;
  onLimitChange: (limit: number) => void;
  rangeLabel: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Selection/sync actions — rendered in the bar's empty middle space so nothing shifts. */
  middle?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  sticky?: boolean;
}

const PAGE_SIZES = [25, 50, 75, 100];

/**
 * Rows-per-page + range on the left, selection/sync actions in the middle
 * (only present when something is selected — otherwise this space is simply
 * empty), page controls on the right. Rendered identically above and below
 * the results so either end of a long list has the same controls.
 */
export default function PaginationBar({
  limit,
  onLimitChange,
  rangeLabel,
  page,
  pageCount,
  onPageChange,
  middle,
  active,
  disabled,
  sticky,
}: PaginationBarProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 1.5, sm: 2.5 },
        py: 1.25,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: active ? (theme: Theme) => alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        position: sticky ? 'sticky' : 'static',
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 2 : undefined,
        flexWrap: 'wrap',
        rowGap: 1,
      }}
    >
      <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8125rem', color: 'text.secondary' }}>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Rows per page</Box>
        <Select
          size="small"
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          sx={{ height: 30, fontSize: '0.8125rem', '& .MuiSelect-select': { py: 0.375, px: 1 } }}
        >
          {PAGE_SIZES.map((size) => (
            <MenuItem key={size} value={size} sx={{ fontSize: '0.8125rem' }}>
              {size}
            </MenuItem>
          ))}
        </Select>
        <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.disabled', fontSize: '0.8125rem' }}>
          {rangeLabel}
        </Typography>
      </Box>

      <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        {middle}
      </Box>

      <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          Page {page} of {Math.max(1, pageCount)}
        </Typography>
        <Pagination
          count={Math.max(1, pageCount)}
          page={page}
          onChange={(_e, value) => onPageChange(value)}
          size="small"
          disabled={disabled}
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Box>
  );
}
